import React, { useState } from 'react';
import { 
  Modal, 
  Upload, 
  Button, 
  Space, 
  message, 
  Typography, 
  Divider,
  Alert,
  Progress,
  List
} from 'antd';
import { 
  UploadOutlined, 
  DownloadOutlined, 
  FileExcelOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useMutation } from 'react-query';
import { contactsAPI } from '../../services/api';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const ImportModal = ({ visible, onCancel, onSuccess }) => {
  const [fileList, setFileList] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const importMutation = useMutation(contactsAPI.import, {
    onSuccess: (data) => {
      setImportResult(data.data?.data);
      setIsImporting(false);
      
      // Fermer automatiquement le modal et rafraîchir les contacts
      if (data.data?.data?.errors === 0) {
        message.success(`${data.data?.data?.imported} contacts importés avec succès`);
        setTimeout(() => {
          handleClose();
          onSuccess(); // Rafraîchir la liste des contacts
        }, 1500);
      } else {
        message.warning(`${data.data?.data?.imported} contacts importés, ${data.data?.data?.errors} erreurs`);
      }
    },
    onError: (error) => {
      setIsImporting(false);
      message.error('Erreur lors de l\'importation: ' + (error.response?.data?.message || error.message));
    }
  });

  const downloadTemplate = async () => {
    try {
      const response = await contactsAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_contacts.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Erreur lors du téléchargement du template');
    }
  };

  const handleImport = () => {
    if (fileList.length === 0) {
      message.error('Veuillez sélectionner un fichier');
      return;
    }

    const formData = new FormData();
    const fileToUpload = fileList[0].originFileObj || fileList[0];
    formData.append('file', fileToUpload);
    
    setIsImporting(true);
    importMutation.mutate(formData);
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.type === 'application/vnd.ms-excel';
      
      if (!isExcel) {
        message.error('Veuillez uploader un fichier Excel (.xlsx ou .xls)');
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('Le fichier doit faire moins de 10MB');
        return false;
      }

      // Create proper file object structure
      const fileObj = {
        uid: file.uid || Date.now().toString(),
        name: file.name,
        status: 'done',
        originFileObj: file
      };
      
      setFileList([fileObj]);
      return false; // Prevent automatic upload
    },
    fileList,
    accept: '.xlsx,.xls'
  };

  const handleClose = () => {
    setFileList([]);
    setImportResult(null);
    setIsImporting(false);
    onCancel();
  };

  return (
    <Modal
      title="Importer des contacts depuis Excel"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Fermer
        </Button>,
        <Button 
          key="template" 
          icon={<DownloadOutlined />}
          onClick={downloadTemplate}
        >
          Télécharger template
        </Button>,
        <Button 
          key="import" 
          type="primary" 
          icon={<UploadOutlined />}
          loading={isImporting}
          onClick={handleImport}
          disabled={fileList.length === 0}
        >
          Importer
        </Button>
      ]}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={4}>Instructions</Title>
          <Paragraph>
            1. Téléchargez le template Excel pour connaître le format requis<br/>
            2. Remplissez vos données dans le fichier en respectant les en-têtes<br/>
            3. Uploadez votre fichier pour importer les contacts
          </Paragraph>
        </div>
        <div>
          <Title level={4}>Télécharger le template</Title>
          <Paragraph>
            Utilisez le template ci-dessous pour vous assurer que vos données sont au bon format.
            Les champs obligatoires sont: Prénom, Nom, Email, Telephone,  Entreprise.
          </Paragraph>
          <Button 
            type="dashed" 
            icon={<FileExcelOutlined />}
            onClick={downloadTemplate}
            block
          >
            Télécharger template_contacts.xlsx
          </Button>
        </div>
        <div>
          <Title level={4}>Uploader votre fichier</Title>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Cliquez ou glissez votre fichier Excel ici</p>
            <p className="ant-upload-hint">
              Supporte les fichiers .xlsx et .xls (max 10MB)
            </p>
          </Dragger>
        </div>

        {isImporting && (
          <div>
            <Title level={4}>Importation en cours...</Title>
            <Progress percent={100} status="active" showInfo={false} />
            <Text type="secondary">Veuillez patienter pendant le traitement du fichier...</Text>
          </div>
        )}

        {importResult && (
          <div>
            <Title level={4}>Résultats de l'importation</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                message={`Importation terminée`}
                description={`${importResult?.imported || 0} contacts importés avec succès${importResult?.errors > 0 ? `, ${importResult.errors} erreurs` : ''}`}
                type={(importResult?.errors || 0) === 0 ? 'success' : 'warning'}
                showIcon
              />
              
              {importResult.errorDetails && importResult.errorDetails.length > 0 && (
                <div>
                  <Title level={5}>Erreurs détaillées:</Title>
                  <List
                    size="small"
                    dataSource={importResult.errorDetails}
                    renderItem={item => (
                      <List.Item>
                        <Text type="danger">{item}</Text>
                      </List.Item>
                    )}
                    style={{ maxHeight: 200, overflow: 'auto' }}
                  />
                </div>
              )}
            </Space>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default ImportModal;
